import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientAPI, wishListAPI } from '../services/api';
import { ArrowLeft, Edit, Trash2, Mail, Phone, MapPin, Building2, Users, FileText, Heart, Plus, ExternalLink, CheckCircle, Clock } from 'lucide-react';

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistsLoading, setWishlistsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchClient();
    fetchWishlists();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await clientAPI.getOne(id);
      setClient(response.data);
    } catch (err) {
      console.error('Error fetching client:', err);
      setFetchError('Client not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlists = async () => {
    try {
      setWishlistsLoading(true);
      const response = await wishListAPI.getAll({ client_id: id });
      setWishlists(response.data);
    } catch (error) {
      console.error('Error fetching wishlists:', error);
    } finally {
      setWishlistsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      try {
        setDeleteError('');
        await clientAPI.delete(id);
        navigate('/clients');
      } catch (err) {
        console.error('Error deleting client:', err);
        setDeleteError('Failed to delete client');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (fetchError || !client) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Client</h3>
          <p className="text-gray-600 mb-6">{fetchError || 'Client not found'}</p>
          <button
            onClick={() => navigate('/clients')}
            className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {deleteError && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-xl">
          <p className="font-medium">{deleteError}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Clients
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-2xl font-semibold shadow-lg hover:bg-red-700 transition-all"
          >
            <Trash2 className="w-5 h-5" />
            Delete
          </button>
          <button
            onClick={() => navigate(`/clients/edit/${id}`)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            <Edit className="w-5 h-5" />
            Edit Client
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{client.name}</h1>
                {client.company && (
                  <p className="text-xl text-gray-600">{client.company}</p>
                )}
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                client.client_type === 'Dealer'
                  ? 'bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700'
                  : 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700'
              }`}>
                {client.client_type === 'Dealer' ? (
                  <Building2 className="w-4 h-4" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
                {client.client_type}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {client.email && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">{client.email}</div>
                    </div>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <div>
                      <div className="text-xs text-gray-500">Phone</div>
                      <div className="font-medium text-gray-900">{client.phone}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Address */}
              {(client.address || client.city || client.state) && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-2xl">
                  <MapPin className="w-5 h-5 text-gray-600 mt-1" />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Address</div>
                    <div className="font-medium text-gray-900">
                      {client.address && <div>{client.address}</div>}
                      <div>
                        {[client.city, client.state, client.postal_code].filter(Boolean).join(', ')}
                      </div>
                      {client.country && <div>{client.country}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            {client.tags && client.tags.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {client.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {client.notes && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Notes</h3>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Client Wish Lists */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Heart className="w-7 h-7 text-rose-600" />
                Client Wish Lists
              </h3>
              <button
                onClick={() => navigate(`/wishlists?client_id=${id}`)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-600 to-rose-500 text-white rounded-xl font-semibold shadow-lg hover:from-rose-700 hover:to-rose-600 transition-all"
              >
                <Plus className="w-5 h-5" />
                Create Wish List
              </button>
            </div>

            {wishlistsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-rose-600 border-t-transparent"></div>
              </div>
            ) : Array.isArray(wishlists) && wishlists.length > 0 ? (
              <div className="space-y-4">
                {wishlists.map((wishlist) => (
                  <div
                    key={wishlist.id}
                    className="border-2 border-gray-100 rounded-2xl p-5 hover:border-rose-200 hover:bg-rose-50/30 transition-all cursor-pointer"
                    onClick={() => navigate(`/wishlists`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900">
                          {wishlist.brand} {wishlist.model || ''}
                        </h4>
                        {wishlist.reference && (
                          <p className="text-sm text-gray-600">Ref: {wishlist.reference}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {wishlist.status === 'Fulfilled' ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Fulfilled
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {wishlist.status}
                          </span>
                        )}
                        {wishlist.match_count > 0 && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                            {wishlist.match_count} matches
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {wishlist.condition && (
                        <div>
                          <span className="text-gray-500">Condition:</span>
                          <span className="ml-2 font-medium text-gray-900">{wishlist.condition}</span>
                        </div>
                      )}
                      {wishlist.max_price && (
                        <div>
                          <span className="text-gray-500">Max Price:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            ${Number(wishlist.max_price).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {(wishlist.year_min || wishlist.year_max) && (
                        <div>
                          <span className="text-gray-500">Year:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {wishlist.year_min || '?'} - {wishlist.year_max || '?'}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Priority:</span>
                        <span className={`ml-2 font-bold ${
                          wishlist.priority === 'High' ? 'text-red-600' :
                          wishlist.priority === 'Medium' ? 'text-amber-600' :
                          'text-gray-600'
                        }`}>
                          {wishlist.priority}
                        </span>
                      </div>
                    </div>

                    {wishlist.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600 line-clamp-2">{wishlist.notes}</p>
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        Created {new Date(wishlist.created_at).toLocaleDateString()}
                      </span>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Heart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No wish lists yet</p>
                <p className="text-sm mt-2">Create a wish list to track what this client is looking for</p>
                <button
                  onClick={() => navigate(`/wishlists?client_id=${id}`)}
                  className="mt-4 px-6 py-3 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-all"
                >
                  Create First Wish List
                </button>
              </div>
            )}
          </div>

          {/* Transaction History */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <FileText className="w-7 h-7 text-blue-600" />
              Purchase History
            </h3>
            {client.purchase_history && client.purchase_history.transactions && client.purchase_history.transactions.length > 0 ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
                    <div className="text-xs text-blue-600 font-semibold mb-1">Total Purchases</div>
                    <div className="text-2xl font-bold text-blue-900">{client.purchase_history.total_purchases}</div>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-2xl">
                    <div className="text-xs text-emerald-600 font-semibold mb-1">Total Spent</div>
                    <div className="text-2xl font-bold text-emerald-900">
                      ${client.purchase_history.total_spent.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl">
                    <div className="text-xs text-purple-600 font-semibold mb-1">Avg Purchase</div>
                    <div className="text-2xl font-bold text-purple-900">
                      ${client.purchase_history.average_purchase.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Transaction List */}
                <div className="space-y-3">
                  {client.purchase_history.transactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="border-2 border-gray-100 rounded-2xl p-4 hover:border-blue-200 hover:bg-blue-50/30 transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900">
                            {transaction.watch_brand} {transaction.watch_model}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {new Date(transaction.sale_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-emerald-600">
                            ${(Number(transaction.sale_price) || 0).toLocaleString()}
                          </div>
                          {transaction.payment_method && (
                            <div className="text-xs text-gray-500 mt-1">{transaction.payment_method}</div>
                          )}
                        </div>
                      </div>
                      {transaction.notes && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-sm text-gray-600">{transaction.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm mt-2">Sales history will appear here</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer-specific info */}
          {client.client_type === 'Customer' && (
            <>
              {(client.budget_min || client.budget_max) && (
                <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Budget Range</h3>
                  <div className="text-3xl font-bold text-emerald-600">
                    ${(client.budget_min || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">to</div>
                  <div className="text-3xl font-bold text-emerald-600">
                    ${(client.budget_max || 0).toLocaleString()}
                  </div>
                </div>
              )}

              {client.preferred_brands && client.preferred_brands.length > 0 && (
                <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Preferred Brands</h3>
                  <div className="space-y-2">
                    {client.preferred_brands.map((brand, index) => (
                      <div
                        key={index}
                        className="p-3 bg-slate-50 rounded-xl text-slate-700 font-medium"
                      >
                        {brand}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Dealer-specific info */}
          {client.client_type === 'Dealer' && (
            <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Dealer Information</h3>
              <div className="space-y-3">
                {client.business_license && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Business License</div>
                    <div className="font-medium text-gray-900">{client.business_license}</div>
                  </div>
                )}
                {client.tax_id && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Tax ID</div>
                    <div className="font-medium text-gray-900">{client.tax_id}</div>
                  </div>
                )}
                {client.dealer_network && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Network</div>
                    <div className="font-medium text-gray-900">{client.dealer_network}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Client Stats */}
          <div className="bg-white rounded-3xl shadow-xl p-6 border-2 border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Spent</span>
                <span className="font-bold text-emerald-600">
                  ${client.purchase_history ? client.purchase_history.total_spent.toLocaleString() : '0'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Transactions</span>
                <span className="font-bold text-gray-900">
                  {client.purchase_history ? client.purchase_history.total_purchases : 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Purchase</span>
                <span className="font-bold text-purple-600">
                  ${client.purchase_history ? client.purchase_history.average_purchase.toLocaleString() : '0'}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                <span className="text-gray-600">Member Since</span>
                <span className="font-bold text-gray-900">
                  {new Date(client.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetailPage;
